import { useFlag } from '@reflag/react-sdk';
import { useQuery } from '@tanstack/react-query';

import type * as api from '@gitbook/api-client';
import { findGitPageURLTarget, matchesGitPageURLTargetPath } from '@gitbook/app-content-refs';
import type { ContentReferenceContext } from '@gitbook/app-database';
import type { HiveClient } from '@gitbook/hive-client';
import { isRevisionPageDocument } from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { getSuspenseSafe, mapSuspenseArray } from '@gitbook/util-suspense-cache';

import { apiQuery, apiResource, getSiteSpacesFromStructure, readAPICache } from '../../state';

/** Resolve repository URLs using the main revisions from the site's cached space metadata. */
export function useGitPageURLReferences(
    refs: api.ContentRef[],
    context: ContentReferenceContext
): Array<{ ref: api.ContentRef; context: ContentReferenceContext }> {
    const hiveClient = useHiveClient();
    const crossSpaceLinking = useFlag('CROSS_SPACE_LINKING');
    const enabled = crossSpaceLinking.isEnabled;
    const allContent = useQuery({
        ...apiQuery(
            'getOrganizationAllContent',
            { organizationId: context.organization },
            { nullOnError: true }
        ),
        enabled: enabled && Boolean(context.site) && refs.some((ref) => ref.kind === 'url'),
    });
    const site = allContent.data?.sites.find((entry) => entry.site.id === context.site);
    const spaces = site
        ? getSiteSpacesFromStructure(site.structure).map((entry) => entry.space)
        : [];
    return resolveGitPageURLReferences(refs, context, hiveClient, enabled ? spaces : []);
}

/** Defer fetching and suspension until the reader is called, including from editor actions. */
export function useGitPageURLReferenceReader(context: ContentReferenceContext) {
    const hiveClient = useHiveClient();
    const crossSpaceLinking = useFlag('CROSS_SPACE_LINKING');
    return useEventCallback((ref: api.ContentRef) => {
        if (!crossSpaceLinking.isEnabled || !context.site || ref.kind !== 'url') {
            return { ref, context };
        }
        const allContent = readAPICache(
            apiResource(
                'getOrganizationAllContent',
                { organizationId: context.organization },
                { nullOnError: true }
            )
        );
        const site = allContent?.sites.find((entry) => entry.site.id === context.site);
        const spaces = site
            ? getSiteSpacesFromStructure(site.structure).map((entry) => entry.space)
            : [];
        return resolveGitPageURLReferences([ref], context, hiveClient, spaces)[0];
    });
}

function resolveGitPageURLReferences(
    refs: api.ContentRef[],
    context: ContentReferenceContext,
    hiveClient: HiveClient,
    spaces: api.Space[]
): Array<{ ref: api.ContentRef; context: ContentReferenceContext }> {
    const targets = refs.map((ref) =>
        context.site && ref.kind === 'url' ? findGitPageURLTarget(ref.url, spaces) : null
    );
    const targetIds = new Set(targets.flatMap((target) => (target ? [target.space] : [])));
    const candidateSpaces = Array.from(
        new Map(
            spaces.filter((space) => targetIds.has(space.id)).map((space) => [space.id, space])
        ).values()
    );
    // Site CRs must select the target member's revision here instead of main.
    const revisions = new Map(
        mapSuspenseArray(
            candidateSpaces,
            (space) =>
                [
                    space.id,
                    getSuspenseSafe(() => hiveClient.store.getRevision(space.id, space.revision)),
                ] as const
        )
    );

    return refs.map((ref, index) => {
        const target = targets[index];
        const revision = target ? revisions.get(target.space) : null;
        const pages =
            target && revision
                ? Object.values(revision.pages).filter(
                      (page) => page.git && matchesGitPageURLTargetPath(target, page.git.filePath)
                  )
                : [];
        const page = pages.length === 1 ? pages[0] : null;
        if (!target || !revision || !page || !isRevisionPageDocument(page)) {
            return { ref, context };
        }
        return {
            ref: target.anchor
                ? { kind: 'anchor', space: target.space, page: page.id, anchor: target.anchor }
                : { kind: 'page', space: target.space, page: page.id },
            context: {
                organization: context.organization,
                site: context.site,
                space: target.space,
                revision: revision.id,
            },
        };
    });
}
